import seon from '@seontechnologies/seon-javascript-sdk';

export type SeonAuthAction = 'sign_up' | 'log_in';

export const SEON_GET_SESSION_TIMEOUT_MS = 2000;
export const SEON_AUTH_SESSION_STASH_KEY = 'seon_auth_session';

type SeonAuthSessionStash = {
  session?: string;
  action: SeonAuthAction;
};

export function initSeonAuthSession(): void {
  try {
    seon.init();
  } catch {
    // CSP / ad blockers must never break auth
  }
}

function stashSeonAuthSession(stash: SeonAuthSessionStash): void {
  try {
    sessionStorage.setItem(SEON_AUTH_SESSION_STASH_KEY, JSON.stringify(stash));
  } catch {
    // Private-mode Safari and friends. Login still proceeds without a blob.
  }
}

export async function captureSeonAuthSession(action: SeonAuthAction): Promise<void> {
  let session: string | undefined;
  try {
    const result = await Promise.race([
      seon.getSession({
        geolocation: { canPrompt: false },
        networkTimeoutMs: SEON_GET_SESSION_TIMEOUT_MS,
        fieldTimeoutMs: SEON_GET_SESSION_TIMEOUT_MS,
        silentMode: true,
      }),
      new Promise<undefined>((resolve) => {
        setTimeout(() => resolve(undefined), SEON_GET_SESSION_TIMEOUT_MS);
      }),
    ]);
    if (typeof result === 'string' && result.length > 0) {
      session = result;
    }
  } catch {
    // Timeout, SDK failure, or CSP — still stash the UI action so login ingest can run.
  }

  stashSeonAuthSession({ action, ...(session ? { session } : {}) });
}

export function takeSeonAuthSessionStash(): { seonAction: SeonAuthAction; seonSession?: string } | undefined {
  try {
    const raw = sessionStorage.getItem(SEON_AUTH_SESSION_STASH_KEY);
    sessionStorage.removeItem(SEON_AUTH_SESSION_STASH_KEY);
    if (!raw) {
      return undefined;
    }

    const parsed = JSON.parse(raw) as SeonAuthSessionStash;
    if (parsed.action !== 'sign_up' && parsed.action !== 'log_in') {
      return undefined;
    }

    return {
      seonAction: parsed.action,
      ...(typeof parsed.session === 'string' && parsed.session.length > 0 ? { seonSession: parsed.session } : {}),
    };
  } catch {
    return undefined;
  }
}
